import { useCallback, useRef } from 'react';

/**
 * Guards an async action against concurrent re-entry — e.g. a user double-clicking
 * a "Create" button before the first click's `setState(true)` has re-rendered the
 * button into its disabled state.
 *
 * The lock is held in a ref, not React state, so re-entry is rejected
 * **synchronously** within the same tick — before any render can occur. A state
 * flag alone cannot do this: state updates are async, so two clicks dispatched back
 * to back both observe the stale `false` and both fire the action.
 *
 * The lock is released when the action settles (resolve *or* reject), via `finally`.
 * Callers that still want a reactive "is submitting" flag for UI (disabled button,
 * spinner) should keep their own state alongside this — `isRunning()` reads the ref
 * and does not trigger re-renders.
 *
 * @param action the async function to guard
 * @returns `run` — invokes `action` unless one is already in flight (then no-ops),
 *          and `isRunning` — a non-reactive read of the in-flight flag.
 */
export function useSingleFlight<Args extends unknown[]>(
  action: (...args: Args) => Promise<void>,
): { run: (...args: Args) => Promise<void>; isRunning: () => boolean } {
  const inFlight = useRef(false);

  const run = useCallback(
    async (...args: Args): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        await action(...args);
      } finally {
        inFlight.current = false;
      }
    },
    [action],
  );

  const isRunning = useCallback(() => inFlight.current, []);

  return { run, isRunning };
}
